/**
 * Offline Queue Service
 *
 * Provides a durable IndexedDB-backed queue for Firestore writes that fail
 * due to network unavailability. Actions are replayed in order when the
 * tech comes back online — either automatically or via a manual "Go Live"
 * button.
 *
 * Design decisions:
 * - IndexedDB (via idb-keyval) survives app restarts, unlike localStorage
 * - Each queued action is self-contained: collection, docId, data, metadata
 * - Replay is sequential (FIFO) to respect ordering (e.g., state transitions)
 * - Failed replays stay in queue with a retry count
 * - AI summary is NOT queued — it's backfilled after sync as a fire-and-forget
 *
 * Storage key: 'offline_action_queue' in the default idb-keyval store
 */

import { get, set } from 'idb-keyval';
import type { FieldValue } from 'firebase/firestore';

// ============================================================================
// TYPES
// ============================================================================

export type OfflineActionType =
  | 'job_state_transition'
  | 'field_log_entry'
  | 'job_update'          // Generic job field update (overrun, scope bypass, etc.)
  | 'return_visit';

export interface OfflineAction {
  /** Unique ID for this queued action */
  id: string;
  /** Type of action — determines how it's replayed */
  type: OfflineActionType;
  /** Firestore collection path */
  collection: string;
  /** Document ID */
  docId: string;
  /** The data payload to write (updateDoc fields) */
  data: Record<string, unknown>;
  /** When the action was queued */
  queuedAt: string;
  /** Number of replay attempts so far */
  retryCount: number;
  /** Human-readable description for UI */
  description: string;
  /** Tech who performed the action */
  techId: string;
  /** Optional: extra metadata for post-sync side effects */
  meta?: Record<string, unknown>;
  /**
   * Schema version of this serialized action. Stamped at enqueue time so
   * replay can detect incompatible shapes between client deploys. Optional
   * because items queued by clients predating this field have no version
   * stamp — those are treated as v1 (the shape at the time they were
   * written). When the shape changes in a breaking way, bump
   * CURRENT_SCHEMA_VERSION and add a migration to handle older versions.
   */
  schemaVersion?: number;
}

export interface OfflineQueueState {
  /** Total actions waiting to sync */
  pendingCount: number;
  /** Actions currently in the queue */
  actions: OfflineAction[];
  /** Whether a sync replay is currently running */
  isSyncing: boolean;
  /** Last sync error, if any */
  lastError: string | null;
  /** Timestamp of last successful sync */
  lastSyncAt: string | null;
  /** Actions that exceeded MAX_RETRIES and were moved to the failed-actions
   *  side-channel. Surfaces in the queue UI so ops can investigate, requeue,
   *  or acknowledge instead of losing the work entirely (C7). */
  failedCount: number;
  failedActions: FailedOfflineAction[];
}

/**
 * An action that exceeded MAX_RETRIES on replay. Persisted to a separate
 * IndexedDB store so the queue itself stays clean while ops still has a
 * record of dropped work. See AUDIT_NOTES.md CRITICAL-#C7.
 */
export interface FailedOfflineAction extends OfflineAction {
  /** When the action was moved out of the live queue. */
  droppedAt: string;
  /** Most recent error message that pushed retryCount to MAX_RETRIES. */
  lastError: string;
  /** ISO timestamp of `lastError`. */
  lastErrorAt: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const QUEUE_KEY = 'offline_action_queue';
const FAILED_ACTIONS_KEY = 'offline_failed_actions';
const LAST_SYNC_KEY = 'offline_last_sync_at';
const MAX_RETRIES = 5;

/**
 * Current OfflineAction shape version. Increment when the serialized action
 * shape changes in a way that older replay code couldn't safely handle (e.g.
 * a renamed field, a new required field, a changed payload structure).
 *
 * Migration policy on replay:
 *   - missing or === current → replay normally
 *   - < current              → run migration before replay
 *   - > current              → unknown shape (downgrade scenario), pause
 *                              replay so an admin can resolve manually
 */
const CURRENT_SCHEMA_VERSION = 1;

// ============================================================================
// QUEUE OPERATIONS (IndexedDB)
// ============================================================================

/**
 * Read the entire queue from IndexedDB.
 */
export async function getQueue(): Promise<OfflineAction[]> {
  try {
    const queue = await get<OfflineAction[]>(QUEUE_KEY);
    return queue || [];
  } catch (err) {
    console.error('[OfflineQueue] Failed to read queue:', err);
    return [];
  }
}

/**
 * Replace the entire queue in IndexedDB.
 */
async function saveQueue(queue: OfflineAction[]): Promise<void> {
  try {
    await set(QUEUE_KEY, queue);
  } catch (err) {
    console.error('[OfflineQueue] Failed to save queue:', err);
  }
}

/**
 * Enqueue an action for later sync.
 * Returns the action ID.
 */
export async function enqueueAction(
  action: Omit<OfflineAction, 'id' | 'queuedAt' | 'retryCount'>
): Promise<string> {
  const id = crypto.randomUUID();
  const entry: OfflineAction = {
    ...action,
    id,
    queuedAt: new Date().toISOString(),
    retryCount: 0,
    schemaVersion: CURRENT_SCHEMA_VERSION,
  };

  const queue = await getQueue();
  queue.push(entry);
  await saveQueue(queue);

  console.log(`[OfflineQueue] Enqueued: ${action.type} — ${action.description}`);

  // Register background sync so the SW can wake us when connectivity returns
  registerBackgroundSync();

  return id;
}

/**
 * Request background sync from the service worker.
 * Fails silently if Background Sync API is not available.
 */
async function registerBackgroundSync(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.ready;
    if (registration && 'sync' in registration) {
      await (registration as ServiceWorkerRegistration & { sync: { register(tag: string): Promise<void> } }).sync.register('sync-offline-queue');
    }
  } catch {
    // Background Sync not supported — auto-sync via useOfflineSync handles it
  }
}

/**
 * Remove a specific action from the queue (after successful replay).
 */
async function dequeueAction(actionId: string): Promise<void> {
  const queue = await getQueue();
  const filtered = queue.filter(a => a.id !== actionId);
  await saveQueue(filtered);
}

/**
 * Read the last successful replay timestamp from IndexedDB. Pre-fix this
 * lived in localStorage, which let the two stores drift (queue cleared,
 * timestamp stuck — or vice versa). Same store as the queue means a single
 * "Clear cached data" wipes both. Tracked as M11.
 */
async function readLastSyncAt(): Promise<string | null> {
  try {
    const value = await get<string>(LAST_SYNC_KEY);
    return value ?? null;
  } catch (err) {
    console.error('[OfflineQueue] Failed to read last_sync_at:', err);
    return null;
  }
}

async function writeLastSyncAt(timestamp: string): Promise<void> {
  try {
    await set(LAST_SYNC_KEY, timestamp);
  } catch (err) {
    console.error('[OfflineQueue] Failed to save last_sync_at:', err);
  }
}

/** Public helper for hooks (useOfflineSync) — same behaviour as readLastSyncAt. */
export async function getLastSyncAt(): Promise<string | null> {
  return readLastSyncAt();
}

/**
 * Read the persistent failed-actions side channel.
 */
async function readFailedActions(): Promise<FailedOfflineAction[]> {
  try {
    const list = await get<FailedOfflineAction[]>(FAILED_ACTIONS_KEY);
    return list || [];
  } catch (err) {
    console.error('[OfflineQueue] Failed to read failed_actions:', err);
    return [];
  }
}

async function saveFailedActions(list: FailedOfflineAction[]): Promise<void> {
  try {
    await set(FAILED_ACTIONS_KEY, list);
  } catch (err) {
    console.error('[OfflineQueue] Failed to save failed_actions:', err);
  }
}

/**
 * Increment the retry count for a failed action.
 * On reaching MAX_RETRIES, persist to the failed-actions side channel
 * BEFORE removing from the live queue so we never silently drop work
 * (C7 — pre-fix the action was just spliced and gone).
 */
async function markRetry(actionId: string, lastError: string): Promise<boolean> {
  const queue = await getQueue();
  const idx = queue.findIndex(a => a.id === actionId);
  if (idx === -1) return false;

  queue[idx].retryCount += 1;
  if (queue[idx].retryCount >= MAX_RETRIES) {
    console.error(`[OfflineQueue] Action ${actionId} exceeded max retries — moving to failed_actions`);
    // Persist to side channel BEFORE splicing the live queue. If the side-
    // channel write fails, readFailedActions/saveFailedActions log + return
    // safely; the queue write below still runs, so the worst case is we
    // log a "moved to failed_actions" line but the action isn't actually
    // there. Better than the pre-fix behaviour where it just vanished.
    const dropped = queue[idx];
    const now = new Date().toISOString();
    const failed = await readFailedActions();
    failed.push({
      ...dropped,
      droppedAt: now,
      lastError,
      lastErrorAt: now,
    });
    await saveFailedActions(failed);
    queue.splice(idx, 1);
    await saveQueue(queue);
    return false; // Dropped
  }

  await saveQueue(queue);
  return true; // Will retry
}

/**
 * Clear the entire live queue (admin/debug only). Does NOT touch the
 * failed-actions side channel — clear that separately via clearFailedActions.
 */
export async function clearQueue(): Promise<void> {
  await saveQueue([]);
  await notifyListeners();
  console.log('[OfflineQueue] Queue cleared');
}

/**
 * Read the failed-actions side channel (actions that exceeded MAX_RETRIES).
 * For UI display + ops investigation.
 */
export async function getFailedActions(): Promise<FailedOfflineAction[]> {
  return readFailedActions();
}

/**
 * Move a failed action back to the live queue with retryCount reset to 0.
 * Used when ops believes the underlying issue is resolved (e.g. customer
 * doc was undeleted, network restored). Returns true if found + requeued.
 */
export async function requeueFailedAction(actionId: string): Promise<boolean> {
  const failed = await readFailedActions();
  const idx = failed.findIndex((a) => a.id === actionId);
  if (idx === -1) return false;

  // Strip the failure metadata before putting back on the live queue.
  const action: OfflineAction = {
    id: failed[idx].id,
    type: failed[idx].type,
    collection: failed[idx].collection,
    docId: failed[idx].docId,
    data: failed[idx].data,
    queuedAt: failed[idx].queuedAt,
    retryCount: 0,
    description: failed[idx].description,
    techId: failed[idx].techId,
    meta: failed[idx].meta,
    schemaVersion: failed[idx].schemaVersion,
  };

  failed.splice(idx, 1);
  const queue = await getQueue();
  queue.push(action);
  await Promise.all([saveFailedActions(failed), saveQueue(queue)]);
  await notifyListeners();
  registerBackgroundSync();
  console.log(`[OfflineQueue] Requeued failed action ${actionId} for retry`);
  return true;
}

/**
 * Acknowledge + permanently remove a failed action. Used when ops decides
 * the work is unrecoverable (e.g. data already manually fixed). Returns
 * true if the action was found and removed.
 */
export async function clearFailedAction(actionId: string): Promise<boolean> {
  const failed = await readFailedActions();
  const idx = failed.findIndex((a) => a.id === actionId);
  if (idx === -1) return false;
  failed.splice(idx, 1);
  await saveFailedActions(failed);
  await notifyListeners();
  console.log(`[OfflineQueue] Cleared failed action ${actionId}`);
  return true;
}

/**
 * Clear ALL failed actions (admin/debug only).
 */
export async function clearAllFailedActions(): Promise<void> {
  await saveFailedActions([]);
  await notifyListeners();
  console.log('[OfflineQueue] All failed actions cleared');
}

// ============================================================================
// REPLAY ENGINE
// ============================================================================

/** Listeners that get notified when queue state changes */
type QueueListener = (state: OfflineQueueState) => void;
const listeners = new Set<QueueListener>();
let currentSyncing = false;
let lastSyncError: string | null = null;

export function subscribeToQueue(listener: QueueListener): () => void {
  listeners.add(listener);
  // Immediately notify with current state
  notifyListeners();
  return () => listeners.delete(listener);
}

async function notifyListeners(): Promise<void> {
  const [queue, failedActions, lastSyncAt] = await Promise.all([
    getQueue(),
    readFailedActions(),
    readLastSyncAt(),
  ]);
  const state: OfflineQueueState = {
    pendingCount: queue.length,
    actions: queue,
    isSyncing: currentSyncing,
    lastError: lastSyncError,
    lastSyncAt,
    failedCount: failedActions.length,
    failedActions,
  };
  listeners.forEach(fn => fn(state));
}

/**
 * Replay all queued actions sequentially.
 * Called when the tech goes back online (auto or manual "Go Live").
 *
 * Returns: number of successfully synced actions.
 */
export async function replayQueue(): Promise<number> {
  if (currentSyncing) {
    console.log('[OfflineQueue] Sync already in progress — skipping');
    return 0;
  }

  const queue = await getQueue();
  if (queue.length === 0) {
    console.log('[OfflineQueue] Nothing to sync');
    return 0;
  }

  currentSyncing = true;
  lastSyncError = null;
  await notifyListeners();

  console.log(`[OfflineQueue] Starting replay of ${queue.length} actions...`);

  // Lazy-import Firestore to avoid circular deps
  const { doc, updateDoc, arrayUnion } = await import('firebase/firestore');
  const { db } = await import('@/firebase');

  let synced = 0;
  // Counts actions whose primary updateDoc succeeded (so they were dequeued
  // and counted as synced) but whose post-sync side effects had at least one
  // internal failure. Pre-fix this was invisible; we now surface it via
  // lastSyncError at the end of the replay so the queue UI shows there's
  // something for ops to look at on the affected job's lastSideEffectError.
  let partialSuccessCount = 0;
  let lastPartialSuccessSummary = '';

  for (const action of queue) {
    // Schema-version gate. Items missing the field were enqueued before this
    // gate existed and are treated as v1 (the only released shape). Items
    // with a version newer than this client knows about are paused — likely a
    // downgrade after a newer client wrote the queue. Don't drop, don't
    // retry-count: leave it for an admin (or an upgraded client) to handle.
    const actionVersion = action.schemaVersion ?? 1;
    if (actionVersion > CURRENT_SCHEMA_VERSION) {
      const msg =
        `Action ${action.id} has schemaVersion ${actionVersion} (this client supports up to ${CURRENT_SCHEMA_VERSION}) — ` +
        `pausing replay. Likely a client downgrade. Upgrade the app or clear the queue manually.`;
      console.warn(`[OfflineQueue] ${msg}`);
      lastSyncError = msg;
      break;
    }
    // Future: if (actionVersion < CURRENT_SCHEMA_VERSION) migrate here.

    try {
      // Reconstruct arrayUnion calls from serialized data
      const payload = reconstructPayload(action.data, arrayUnion);

      await updateDoc(doc(db, action.collection, action.docId), payload);
      await dequeueAction(action.id);
      synced++;
      console.log(`[OfflineQueue] ✓ Synced: ${action.description}`);

      // Post-sync side effects. Failures here are NOT requeued — most effects
      // append via arrayUnion with random IDs so a blanket retry would create
      // duplicates (extra activity-log entries, duplicate follow-up jobs).
      // Instead we surface the partial-success state to the queue UI; details
      // already live on the job's lastSideEffectError for manual recovery.
      const postSyncErrors = await runPostSyncEffects(action);
      if (postSyncErrors.length > 0) {
        partialSuccessCount++;
        lastPartialSuccessSummary = `${action.description} — ${postSyncErrors
          .map((e) => `${e.source}: ${e.message}`)
          .join(' | ')}`;
        console.warn(
          `[OfflineQueue] ⚠ Post-sync had ${postSyncErrors.length} error(s) for action ${action.id}: ${lastPartialSuccessSummary}`,
        );
      }

    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error(`[OfflineQueue] ✗ Failed: ${action.description} — ${msg}`);
      lastSyncError = `${action.description}: ${msg}`;

      const willRetry = await markRetry(action.id, msg);
      if (!willRetry) {
        console.error(`[OfflineQueue] Dropped action after ${MAX_RETRIES} retries: ${action.id} — see failed_actions side channel`);
      }
      // Stop on first failure — don't skip ahead (ordering matters)
      break;
    }
  }

  // Don't clobber a primary-write failure (which already set lastSyncError);
  // only annotate when the replay otherwise completed but had partial successes.
  if (!lastSyncError && partialSuccessCount > 0) {
    lastSyncError =
      `${partialSuccessCount} action(s) synced but had post-sync errors — ` +
      `last: ${lastPartialSuccessSummary}`;
  }

  if (synced > 0) {
    await writeLastSyncAt(new Date().toISOString());
  }

  currentSyncing = false;
  await notifyListeners();

  console.log(`[OfflineQueue] Replay complete: ${synced}/${queue.length} synced`);
  return synced;
}

// ============================================================================
// PAYLOAD RECONSTRUCTION
// ============================================================================

/**
 * Firestore's arrayUnion() can't be serialized to IndexedDB.
 * We use a convention: fields prefixed with `__arrayUnion__` in the stored
 * data get wrapped in arrayUnion() during replay.
 *
 * Example stored: { "__arrayUnion__fieldLog": { id: "...", text: "..." } }
 * Replayed as:    { fieldLog: arrayUnion({ id: "...", text: "..." }) }
 */
function reconstructPayload(
  data: Record<string, unknown>,
  arrayUnionFn: (...elements: unknown[]) => FieldValue
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (key.startsWith('__arrayUnion__')) {
      const realKey = key.replace('__arrayUnion__', '');
      result[realKey] = arrayUnionFn(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

// ============================================================================
// POST-SYNC SIDE EFFECTS
// ============================================================================

/**
 * After a queued action's updateDoc replays successfully, run the same side
 * effects the online path would have run (deal sync, fault sync, install
 * completion, service activation, return-visit follow-up, etc.).
 *
 * All side-effect logic lives in job-side-effects.service.ts so the online
 * path (transitionJobState, completeWithReturnVisit) and the offline replay
 * path share one implementation. See AUDIT_NOTES.md SP8 for background.
 *
 * Returns the side-effect errors so the replay loop can surface partial-
 * success cases (primary write committed but some downstream effect failed)
 * to the queue UI instead of silently treating the action as fully synced.
 */
async function runPostSyncEffects(
  action: OfflineAction,
): Promise<Array<{ source: string; message: string }>> {
  if (action.type !== 'job_state_transition' && action.type !== 'return_visit') return [];
  const { executeJobSideEffects } = await import('./job-side-effects.service');
  const result = await executeJobSideEffects(action);
  return result.errors;
}

// ============================================================================
// HELPER: Check if we should queue (offline detection)
// ============================================================================

/**
 * Quick check for whether we're offline.
 * Uses navigator.onLine as primary signal.
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}
